import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import Osd from './Osd';
import Toolbar from './Toolbar';
import Options from './Options';

function Viewer() {
  const [osdInstance, setOsdInstance] = useState(null);
  const [images, setImages] = useState([]);
  const [hasParsedImagesFromAttr, setHasParsedImagesFromAttr] = useState(false);
  const [viewerClasses, setViewerClasses] = useState([]);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [isOptionsOpen, setIsOptionsOpen] = useState(false);
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [imageManipulations, setImageManipulations] = useState({
    Brightness: 100,
    Contrast: 100,
    Saturation: 100,
    Hue: 0,
    Grayscale: false,
    Invert: false,
    Rotation: 0,
  });
  const viewerRef = useRef();
  const optionsButtonRef = useRef();

  const exitFullScreenHandler = (e) => {
    // For non-supporting browsers requestFullscreen is unavailable
    if (
      'requestFullscreen' in viewerRef.current === false &&
      e.type === 'keydown' &&
      e.key === 'Escape'
    ) {
      setIsFullScreen(false);
    }
    // Esc or the browser inserted close button will trigger the fullscreenchange event instead
    if (e.type === 'fullscreenchange' && !document.fullscreenElement) {
      setIsFullScreen(false);
    }
  };

  // Parse the images stored in the images data attr
  const parseImagesFromAttrs = () => {
    const imageStringGroups =
      viewerRef.current.parentElement.dataset.images.split('|');
    imageStringGroups.forEach((stringGroup) => {
      setImages((images) => {
        return [
          ...images,
          {
            label: stringGroup
              .split('__label__')
              .pop()
              .split('__url__')
              .shift(),
            url: `${stringGroup.split('__url__').pop()}/info.json`,
          },
        ];
      });
    });
    setHasParsedImagesFromAttr(true);
  };

  // Fullscreen enter / exit
  useEffect(() => {
    const classList = ['viewer'];
    if (isFullScreen) classList.push('fullscreen');
    setViewerClasses(classList);

    if (isFullScreen) {
      document.addEventListener('keydown', exitFullScreenHandler);
      document.body.classList.add('fullscreen');
    } else {
      document.body.classList.remove('fullscreen');
    }

    return () => {
      document.removeEventListener('keydown', exitFullScreenHandler);
    };
  }, [isFullScreen]);

  // Mount / unmount
  useEffect(() => {
    parseImagesFromAttrs();
    document.addEventListener('fullscreenchange', exitFullScreenHandler);

    return () => {
      document.removeEventListener('fullscreenchange', exitFullScreenHandler);
    };
  }, []);

  return (
    <div ref={viewerRef} id="viewer" className={viewerClasses.join(' ')}>
      {hasParsedImagesFromAttr ? (
        <>
          {osdInstance ? (
            <>
              <Toolbar
                osdInstance={osdInstance}
                setIsFullScreen={setIsFullScreen}
                setIsOptionsOpen={setIsOptionsOpen}
                isOptionsOpen={isOptionsOpen}
                optionsButtonRef={optionsButtonRef}
              />
              <Options
                osdInstance={osdInstance}
                images={images}
                setIsOptionsOpen={setIsOptionsOpen}
                isOptionsOpen={isOptionsOpen}
                optionsButtonRef={optionsButtonRef}
                imageManipulations={imageManipulations}
                setImageManipulations={setImageManipulations}
                activeImageIndex={activeImageIndex}
                setActiveImageIndex={setActiveImageIndex}
              />
            </>
          ) : null}
          <Osd
            osdInstance={osdInstance}
            setOsdInstance={setOsdInstance}
            images={images}
            isFullScreen={isFullScreen}
            setIsFullScreen={setIsFullScreen}
            imageManipulations={imageManipulations}
            setImageManipulations={setImageManipulations}
            activeImageIndex={activeImageIndex}
            setActiveImageIndex={setActiveImageIndex}
          />
        </>
      ) : null}
    </div>
  );
}

export default document.addEventListener('DOMContentLoaded', function () {
  ReactDOM.render(
    <React.StrictMode>
      <Viewer />
    </React.StrictMode>,
    document.querySelector('#viewer-container')
  );
});
